import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable';
import {ButtonIcon} from '@pepsico-ds/ui';
import {useAppSelector} from '@/store';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_CODES} from '@/utils/constants/globalConstants';
import styles from './ProductsTable.module.scss';
import {COLOR_CODE} from '@/utils/constants/colors';
import {Constants, DISABLE_HEADERS} from '@/utils/constants/commonConstants';

/**
 * ProductsTable component displays the products in a data table format
 * with columns for Product ID, Product Name, Details, Priority, and Actions
 */
const ProductsTable = ({
  products = [],
  productsLoading = false,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete
}) => {
  const countryCode = useAppSelector(selectCountryCode);

  // Render details column with color coding
  const renderDetails = (details) => {
    // Add null checks to prevent errors
    if (!details) {
      return (
        <div className='flex flex-col gap-1'>
          <div style={{color: COLOR_CODE._FF9533}}>Points: Add</div>
          <div style={{color: COLOR_CODE._FF9533}}>Quota: Add</div>
          <div style={{color: COLOR_CODE._FF9533}}>Inventory: Add</div>
        </div>
      );
    }

    const getValueColor = (value) =>
      value ? COLOR_CODE._EFEFEF : COLOR_CODE._FF9533;
    const getQuotaText = (quota) => {
      if (!quota) return 'Add';
      return quota.unlimited ? 'Unlimited' : quota.value || 'Add';
    };
    const getQuotaValue = (quota) => {
      if (!quota) return '';
      return quota.unlimited ? Constants.Unlimited : quota.value || '';
    };

    return (
      <div className='flex flex-col gap-1'>
        <div style={{color: getValueColor(details.points)}}>
          Points: {details.points || 'Add'}
        </div>
        <div style={{color: getValueColor(getQuotaValue(details.quota))}}>
          Quota: {getQuotaText(details.quota)}
        </div>
        <div style={{color: getValueColor(details.inventory)}}>
          Inventory: {details.inventory || 'Add'}
        </div>
      </div>
    );
  };

  // Define table columns
  const columns = useMemo(() => {
    // Render actions column with icon buttons
    const renderActions = (value, record, index) => {
      const isFirst = index === 0;
      const isLast = products.length - 1 === index;

      return (
        <div className='flex items-center gap-2'>
          {/* Move Up Button */}
          <ButtonIcon
            icon='arrow_upward'
            isDark
            type='default'
            disabled={isFirst}
            onClick={() => onMoveUp(index)}
            data-testid={`move-up-${index}`}
          />

          {/* Move Down Button */}
          <ButtonIcon
            icon='arrow_downward'
            isDark
            type='default'
            disabled={isLast}
            onClick={() => onMoveDown(index)}
            data-testid={`move-down-${index}`}
          />

          {/* Edit Button */}
          <ButtonIcon
            icon='edit'
            isDark
            type='default'
            onClick={() => onEdit(value, index)}
            data-testid={`edit-${index}`}
          />

          {/* Delete Button */}
          <ButtonIcon
            icon='delete'
            isDark
            type='default'
            onClick={() => onDelete(index)}
            data-testid={`delete-${index}`}
          />
        </div>
      );
    };

    const columns = [
      {
        key: 'productId',
        label: 'PRODUCT ID',
        label_of_column: 'PRODUCT ID',
        name_of_db_column: 'productId',
        hideSortIcon: true,
        filterable: false,
        render: (value) => {
          return <span style={{color: '#3387DF'}}>{value?.productId}</span>;
        }
      }
    ];

    // Add GTIN column for Mexico
    if (countryCode === COUNTRY_CODES.MEXICO) {
      columns.push({
        key: 'gtin',
        label: 'GTIN',
        label_of_column: 'GTIN',
        name_of_db_column: 'gtin',
        hideSortIcon: true,
        filterable: false,
        render: (value) => (
          <span className='text-white'>{value?.gtin || '-'}</span>
        )
      });
    }

    columns.push(
      {
        key: 'productName',
        label: 'PRODUCT NAME',
        label_of_column: 'PRODUCT NAME',
        name_of_db_column: 'productName',
        filterable: false,
        hideSortIcon: true,
        render: (value) => (
          <span className='text-white'>{value?.productName}</span>
        )
      },
      {
        key: 'details',
        label: 'DETAILS',
        label_of_column: 'DETAILS',
        name_of_db_column: 'details',
        hideSortIcon: true,
        filterable: false,
        render: (value) => renderDetails(value?.details)
      },
      {
        key: 'priority',
        label: 'PRIORITY',
        label_of_column: 'PRIORITY',
        name_of_db_column: 'priority',
        hideSortIcon: true,
        filterable: false,
        render: (value) => <span className='text-white'>{value?.priority}</span>
      },
      {
        key: 'actions',
        label: 'ACTIONS',
        label_of_column: '',
        name_of_db_column: 'actions',
        hideSortIcon: true,
        filterable: false,
        render: (value, record, index) => renderActions(value, record, index)
      }
    );

    return columns;
  }, [products.length, onMoveUp, onMoveDown, onEdit, onDelete, countryCode]);

  // Transform products data for the table
  const tableData = useMemo(() => {
    return products.map((product, index) => ({
      ...product,
      // Add index for actions rendering
      _index: index
    }));
  }, [products]);

  return (
    <div className={`w-full ${styles.rewardsProductsTable}`}>
      <CustomDataTable
        columns={columns}
        records={tableData}
        isLoading={productsLoading}
        pageSize={10}
        totalRecords={tableData.length}
        paginationConfig={{
          pageIndex: 0,
          hidePagination: true
        }}
        filterConfig={{
          hideFilterRow: true,
          disabledHeaders: DISABLE_HEADERS.PRODUCTS_TABLE_HEADERS
        }}
        selectionConfig={{
          hideSelectAll: true,
          hideCheckbox: true
        }}
        toolbarConfig={{
          hideToolbar: true,
          showActionMenu: false
        }}
        displayConfig={{
          tableMinWidth: 800,
          emptyStateConfig: {
            title: 'No products found',
            hideDescription: true,
            hideButton: true
          }
        }}
      />
    </div>
  );
};

ProductsTable.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      productName: PropTypes.string,
      gtin: PropTypes.string,
      priority: PropTypes.number,
      details: PropTypes.shape({
        points: PropTypes.string,
        quota: PropTypes.shape({
          unlimited: PropTypes.bool,
          value: PropTypes.string
        }),
        inventory: PropTypes.string
      })
    })
  ),
  productsLoading: PropTypes.bool,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ProductsTable;
